import React, { useState, useRef, useEffect } from 'react';

const AudioRecorder = ({ onSend, onCancel, uploading }) => {
    const [recording, setRecording]   = useState(false);
    const [duration, setDuration]     = useState(0);
    const [audioBlob, setAudioBlob]   = useState(null);
    const [audioUrl, setAudioUrl]     = useState(null);
    const [error, setError]           = useState('');

    const mediaRecorderRef = useRef(null);
    const chunksRef        = useRef([]);
    const timerRef         = useRef(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearInterval(timerRef.current);
            if (mediaRecorderRef.current && recording) {
                mediaRecorderRef.current.stop();
            }
            if (audioUrl) URL.revokeObjectURL(audioUrl);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startRecording = async () => {
        try {
            setError('');
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
            mediaRecorderRef.current = mr;
            chunksRef.current = [];

            mr.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data);
            };
            mr.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
                const url  = URL.createObjectURL(blob);
                setAudioBlob(blob);
                setAudioUrl(url);
                stream.getTracks().forEach(t => t.stop());
            };

            mr.start();
            setRecording(true);
            setDuration(0);
            timerRef.current = setInterval(() => {
                setDuration(d => {
                    if (d >= 120) { stopRecording(); return d; } // max 2 min
                    return d + 1;
                });
            }, 1000);
        } catch (err) {
            setError('Microphone access denied. Please allow mic in browser settings.');
        }
    };

    const stopRecording = () => {
        clearInterval(timerRef.current);
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        setRecording(false);
    };

    const handleSend = () => {
        if (audioBlob) onSend(audioBlob, duration);
    };

    const handleDiscard = () => {
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setAudioBlob(null);
        setAudioUrl(null);
        setDuration(0);
        setRecording(false);
        onCancel();
    };

    const formatTime = (s) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

    return (
        <div className="audio-recorder">
            {error && <div className="audio-error">{error}</div>}

            <div className="audio-recorder-inner">
                {!audioBlob ? (
                    <>
                        <div className={`audio-pulse ${recording ? 'audio-pulse--active' : ''}`}>
                            🎙️
                        </div>
                        <span className="audio-timer">{formatTime(duration)}</span>
                        {!recording ? (
                            <button className="audio-btn audio-btn--record" onClick={startRecording}>
                                Start Recording
                            </button>
                        ) : (
                            <button className="audio-btn audio-btn--stop" onClick={stopRecording}>
                                ⏹ Stop
                            </button>
                        )}
                        <button className="audio-btn audio-btn--cancel" onClick={handleDiscard}>
                            Cancel
                        </button>
                    </>
                ) : (
                    <>
                        <audio controls src={audioUrl} className="audio-preview" />
                        <span className="audio-duration">Duration: {formatTime(duration)}</span>
                        <button
                            className="audio-btn audio-btn--send"
                            onClick={handleSend}
                            disabled={uploading}
                        >
                            {uploading ? '⏳ Uploading...' : '➤ Send Voice Note'}
                        </button>
                        <button className="audio-btn audio-btn--cancel" onClick={handleDiscard}>
                            🗑 Discard
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default AudioRecorder;
